// src/planner/EventNode.js
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ActionButton, Description, SubTaskList, SubListTitle, DueDate, EventTitle, Check, fadeIn } from './plannerStyles';
import { color } from '../../theme/tokens';

const NodeWrapper = styled.div.attrs(props => ({
    style: {
        left: `${props.$position.x}px`,
        top: `${props.$position.y}px`,
    }
}))`
    position: absolute;
    width: 300px;
    box-sizing: border-box;
    background: ${color.surface};
    border: 1px solid ${color.line};
    border-top: 2px solid ${props => props.$borderColor || color.text3};
    transition: border-color 0.25s ease, background-color 0.25s ease, opacity 0.25s ease;
    z-index: 10;
    box-shadow: 0 8px 24px rgba(7, 2, 5, 0.6);

    &:hover {
        z-index: 100;
        background: ${color.surface2};
        border-left-color: ${color.line2};
        border-right-color: ${color.line2};
        border-bottom-color: ${color.line2};
    }

    ${({ $isCompleted }) => $isCompleted && css`
        opacity: 0.6;
        filter: grayscale(0.8);
    `}

    ${({ $isExpanded }) => $isExpanded && css`
        min-height: auto;
    `}
`;

const NodeHeader = styled.div`
    padding: 12px 14px;
    cursor: pointer;
    user-select: none;
`;

const NodeBody = styled.div`
    padding: 12px 14px 14px;
    border-top: 1px solid ${color.line};
    animation: ${fadeIn} 0.2s ease-out;
`;

const ActionButtonsContainer = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 14px;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

const UndatedSubtaskItem = styled.div`
    font-size: 13px;
    padding: 6px 0;
    color: ${color.text2};
    border-bottom: 1px solid ${color.line};
    display: flex;
    justify-content: space-between;
    align-items: center;
    &:last-child { border-bottom: none; }
`;

const SubtaskContent = styled.div`
    flex-grow: 1;
    min-width: 0;
    margin-right: 10px;
    display: flex;
    align-items: center;
    gap: 8px;

    ${({ $isCompleted }) => $isCompleted && css`
        text-decoration: line-through;
        color: ${color.text3};
    `}
`;

const ExpandIndicator = styled.span`
    margin-left: auto;
    font-size: 10px;
    color: ${color.text3};
    transition: transform 0.2s ease;
    ${({ $isExpanded }) => $isExpanded && css`transform: rotate(180deg);`}
`;

const smallBtn = { minHeight: 24, padding: '0 6px', fontSize: 11 };

function SimpleEventItem({ event, onEdit, onDelete, onToggleComplete }) {
    return (
        <UndatedSubtaskItem>
            <SubtaskContent $isCompleted={event.is_completed}>
                <Check
                    checked={event.is_completed}
                    onChange={(e) => { e.stopPropagation(); onToggleComplete(event); }}
                    aria-label={`完成：${event.title}`}
                />
                {event.title}
            </SubtaskContent>
            <div style={{display:'flex', gap:'4px'}}>
                <ActionButton type="button" onClick={(e) => {e.stopPropagation(); onEdit(event);}} style={smallBtn}>编辑</ActionButton>
                <ActionButton type="button" $danger onClick={(e) => {e.stopPropagation(); onDelete(event.id);}} style={smallBtn}>删除</ActionButton>
            </div>
        </UndatedSubtaskItem>
    );
}

function EventNode({ event, position, allEvents, expandedNodes, onNodeExpand, onToggleComplete, onEdit, onDelete, onAddSubtask }) {
    const isExpanded = expandedNodes?.has(event.id) || false;
    const undatedChildren = useMemo(() => allEvents.filter(e => e.parent_id === event.id && !e.due_date), [allEvents, event.id]);
    const canBeCompleted = useMemo(() => {
        const allChildren = allEvents.filter(e => e.parent_id === event.id);
        return allChildren.length === 0 || allChildren.every(child => child.is_completed);
    }, [allEvents, event.id]);

    return (
        <NodeWrapper $position={position} $isCompleted={event.is_completed} $isExpanded={isExpanded} $borderColor={event.color}>
            <NodeHeader onClick={(e) => { e.stopPropagation(); if (onNodeExpand) onNodeExpand(event.id, !isExpanded); }}>
                <EventTitle $isCompleted={event.is_completed}>
                    <Check
                        checked={event.is_completed}
                        onChange={(e) => { e.stopPropagation(); onToggleComplete(event); }}
                        onClick={e => e.stopPropagation()}
                        disabled={!canBeCompleted && !event.is_completed}
                        aria-label={`完成：${event.title}`}
                    />
                    {event.title}
                    <ExpandIndicator $isExpanded={isExpanded} aria-hidden="true">▼</ExpandIndicator>
                </EventTitle>
                {event.due_date && (
                    <DueDate style={{marginTop: '8px', display:'block'}}>
                        {new Date(event.due_date).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </DueDate>
                )}
            </NodeHeader>

            {isExpanded && (
                <NodeBody>
                    <Description>{event.description || '暂无描述。'}</Description>

                    {undatedChildren.length > 0 && (
                        <SubTaskList>
                            <SubListTitle>子项<b>{undatedChildren.length}</b></SubListTitle>
                            {undatedChildren.map(child => (
                                <SimpleEventItem key={child.id} event={child} onEdit={onEdit} onDelete={onDelete} onToggleComplete={onToggleComplete} />
                            ))}
                        </SubTaskList>
                    )}

                    <ActionButtonsContainer>
                        <ActionButton type="button" onClick={(e) => { e.stopPropagation(); onEdit(event); }}>编辑</ActionButton>
                        <ActionButton type="button" onClick={(e) => { e.stopPropagation(); onAddSubtask(event); }}>添加子项</ActionButton>
                        <ActionButton type="button" $danger onClick={(e) => { e.stopPropagation(); onDelete(event.id); }}>删除</ActionButton>
                    </ActionButtonsContainer>
                </NodeBody>
            )}
        </NodeWrapper>
    );
}

export default React.memo(EventNode);
